if (!customElements.get('ltv-predictive-search')) {
  customElements.define('ltv-predictive-search', class extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      this.input = this.querySelector('[name="q"]');
      this.panel = this.querySelector('.ltv-search-results');
      this.list = this.querySelector('[role="listbox"]');
      this.status = this.querySelector('[role="status"]');
      this.cache = new Map();
      this.generation = 0;
      this.active = -1;
      this.pointerInPanel = false;
      const options = { signal: this.listeners.signal };
      this.input.addEventListener('input', () => this.schedule(), options);
      this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.schedule(); }, options);
      this.input.addEventListener('keydown', event => this.keydown(event), options);
      this.panel.addEventListener('pointerdown', () => { this.pointerInPanel = true; }, options);
      const releasePointer = () => {
        this.pointerInPanel = false;
        this.deferClose();
      };
      document.addEventListener('pointerup', releasePointer, options);
      document.addEventListener('pointercancel', releasePointer, options);
      this.addEventListener('focusout', () => {
        this.deferClose();
      }, options);
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, options);
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
      clearTimeout(this.blurTimer);
      this.listeners = null;
    }
    deferClose() {
      clearTimeout(this.blurTimer);
      this.blurTimer = setTimeout(() => {
        if (!this.pointerInPanel && !this.contains(document.activeElement)) this.close();
      }, 0);
    }
    close() {
      clearTimeout(this.timer);
      clearTimeout(this.blurTimer);
      this.request?.abort();
      this.generation++;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.list.removeAttribute('aria-busy');
      this.active = -1;
    }
    show() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    schedule() {
      this.close();
      const query = this.input.value.trim();
      this.list.replaceChildren();
      this.status.textContent = '';
      if (!query) return;
      const generation = this.generation;
      this.timer = setTimeout(() => this.search(query, generation), 250);
    }
    async search(query, generation) {
      this.message(this.dataset.loading);
      this.list.setAttribute('aria-busy', 'true');
      this.show();
      try {
        let results = this.cache.get(query.toLocaleLowerCase());
        if (!results) {
          this.request = new AbortController();
          const url = new URL(this.dataset.url, location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product,query,collection');
          url.searchParams.set('resources[limit]', '8');
          const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Predictive search unavailable');
          results = (await response.json()).resources?.results;
          if (!results) throw new Error('Invalid predictive response');
          this.cache.set(query.toLocaleLowerCase(), results);
          if (this.cache.size > 30) this.cache.delete(this.cache.keys().next().value);
        }
        if (generation !== this.generation || !this.contains(document.activeElement)) return;
        this.render(results, query);
      } catch (error) {
        if (error.name === 'AbortError' || generation !== this.generation) return;
        this.message(this.dataset.error);
        this.addAll(query);
      } finally {
        if (generation === this.generation) this.list.removeAttribute('aria-busy');
      }
    }
    message(text) {
      this.list.replaceChildren();
      const message = document.createElement('p');
      message.className = 'ltv-search-message';
      message.textContent = text;
      this.list.append(message);
      this.status.textContent = text;
    }
    safeURL(value) {
      if (!value) return null;
      try {
        const url = new URL(value, location.origin);
        return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
      } catch { return null; }
    }
    option(label, url, product) {
      const href = this.safeURL(url);
      if (!href) return;
      const a = document.createElement('a');
      a.href = href;
      a.className = 'ltv-search-option';
      a.id = `${this.list.id}-option-${this.list.querySelectorAll('[role="option"]').length}`;
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      a.tabIndex = 0;
      if (product) {
        a.classList.add('ltv-search-product');
        const image = this.safeURL(product.featured_image?.url || product.image);
        if (image) {
          const img = document.createElement('img');
          img.src = image;
          img.alt = '';
          img.width = 64;
          img.height = 64;
          img.loading = 'lazy';
          a.append(img);
        }
      }
      const copy = document.createElement('span');
      copy.className = 'ltv-search-option-copy';
      const title = document.createElement('span');
      title.className = 'ltv-search-option-title';
      title.textContent = label;
      copy.append(title);
      if (product && Number.isFinite(Number(product.price))) {
        const price = document.createElement('strong');
        try { price.textContent = new Intl.NumberFormat(this.dataset.locale || 'es', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(Number(product.price)); }
        catch { price.textContent = `${Number(product.price).toFixed(2)} ${this.dataset.currency || 'USD'}`; }
        copy.append(price);
      }
      a.append(copy);
      this.list.append(a);
    }
    addAll(query) {
      const url = new URL(this.dataset.searchUrl, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      this.option(`${this.dataset.all}: “${query}”`, url.href);
      this.list.lastElementChild?.classList.add('ltv-search-all');
    }
    render(results, query) {
      this.list.replaceChildren();
      let count = 0;
      for (const [key, label] of [['products', this.dataset.products], ['collections', this.dataset.collections], ['queries', this.dataset.queries]]) {
        const items = Array.isArray(results[key]) ? results[key].slice(0, 8) : [];
        if (!items.length) continue;
        const heading = document.createElement('p');
        heading.className = 'ltv-search-heading';
        heading.textContent = label;
        this.list.append(heading);
        for (const item of items) {
          this.option(item.title || item.text, item.url, key === 'products' ? item : null);
          count++;
        }
      }
      if (!count) this.message(this.dataset.empty);
      this.addAll(query);
      this.status.textContent = count ? `${count} ${this.list.getAttribute('aria-label')}` : this.dataset.empty;
      this.show();
    }
    keydown(event) {
      if (event.key === 'Escape') { this.close(); return; }
      const options = [...this.list.querySelectorAll('[role="option"]')];
      if (this.panel.hidden || !options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.active = (this.active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
        this.input.setAttribute('aria-activedescendant', options[this.active].id);
        options[this.active].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.active >= 0) {
        event.preventDefault();
        options[this.active].click();
      }
    }
  });
}

function initLtvNavigation(root = document) {
  root.querySelectorAll('.ltv-header-updated').forEach(header => {
    if (header.dataset.navigationReady) return;
    header.dataset.navigationReady = 'true';
    const dropdowns = [...header.querySelectorAll('.ltv-nav-dropdown')];
    dropdowns.forEach(details => {
      details.addEventListener('toggle', () => {
        if (details.open) dropdowns.forEach(other => { if (other !== details) other.open = false; });
      });
      details.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && matchMedia('(hover: hover)').matches) details.open = true;
      });
      details.addEventListener('pointerleave', () => {
        if (!details.contains(document.activeElement)) details.open = false;
      });
      details.addEventListener('focusout', () => queueMicrotask(() => {
        if (!details.contains(document.activeElement)) details.open = false;
      }));
    });
    const mobile = header.querySelector('.ltv-mobile-menu');
    header.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const details = event.target.closest('details[open]');
      if (details) { details.open = false; details.querySelector('summary').focus(); }
    });
    document.addEventListener('pointerdown', event => {
      if (!header.contains(event.target)) {
        dropdowns.forEach(details => { details.open = false; });
        if (mobile) mobile.open = false;
      }
    });
  });
}
initLtvNavigation();
document.addEventListener('shopify:section:load', event => initLtvNavigation(event.target));

